import React, { useRef } from 'react';

/**
 * PINInput — 6 individual digit boxes with auto-advance and backspace navigation.
 *
 * Props
 *   value    : string  — the current 6-char digit string
 *   onChange : fn(str) — called with the new full digit string
 *   disabled : bool
 *   size     : 'sm' | 'md' (default 'md')
 */
export const PINInput = ({ value = '', onChange, disabled = false, size = 'md' }) => {
  const refs    = useRef([]);
  const digits  = value.split('');
  const boxSize = size === 'sm' ? { width: 36, height: 44, fontSize: 16 }
                                : { width: 44, height: 54, fontSize: 22 };

  const update = (idx, char) => {
    const d = char.replace(/\D/g, '').slice(-1);
    if (!d) return;
    const next = [...Array(6)].map((_, i) => digits[i] || '');
    next[idx] = d;
    onChange(next.join(''));
    if (idx < 5) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      const next = [...Array(6)].map((_, i) => digits[i] || '');
      if (next[idx]) {
        next[idx] = '';
        onChange(next.join(''));
      } else if (idx > 0) {
        refs.current[idx - 1]?.focus();
      }
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      onChange(pasted.padEnd(6, '').slice(0, 6).replace(/ /g, ''));
      refs.current[Math.min(pasted.length, 5)]?.focus();
    }
    e.preventDefault();
  };

  return (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
      {[0, 1, 2, 3, 4, 5].map(i => (
        <input
          key={i}
          ref={el => (refs.current[i] = el)}
          type="tel"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] || ''}
          onChange={e => update(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          disabled={disabled}
          autoComplete="one-time-code"
          style={{
            width:           boxSize.width,
            height:          boxSize.height,
            textAlign:       'center',
            fontSize:        boxSize.fontSize,
            fontWeight:      700,
            fontFamily:      'monospace',
            backgroundColor: digits[i] ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.06)',
            border:          digits[i] ? '1.5px solid #3B82F6' : '1px solid rgba(255,255,255,0.2)',
            borderRadius:    8,
            color:           '#fff',
            outline:         'none',
            caretColor:      '#3B82F6',
            transition:      'border-color 0.15s, background 0.15s',
            opacity:         disabled ? 0.5 : 1,
            cursor:          disabled ? 'not-allowed' : 'text',
          }}
        />
      ))}
    </div>
  );
};

export default PINInput;
